import type { ErrorEvent, EventHint } from '@sentry/vue';

// Kept dependency-free: the embedded CX and signup entries import this for their Sentry filter.

// What each engine rejects a fetch with when no HTTP response arrived: the browser canceled it
// (navigation, a page reload, a closed tab) or the host couldn't be reached. Both look the same from
// the page, and neither is an application fault worth an issue — gateway logs cover a real outage.
// Anchored, with an optional `TypeError: ` / `AbortError: ` prefix for the OpenAPI client's wrapper
// ("TypeError: Failed to fetch (rmm.api.zyno.app)"), so the chunk-load messages that start the same
// way ("Failed to fetch dynamically imported module") stay with their own filter.
const NO_RESPONSE_FETCH_PATTERNS = [
    /^(TypeError: )?Failed to fetch( \([^)]*\))?$/, // Chromium, optionally naming the host
    /^(TypeError: )?NetworkError when attempting to fetch resource\.?$/, // Firefox
    /^(TypeError: )?Load failed( \([^)]*\))?$/, // Safari
    /^(AbortError: )?The user aborted a request\.?$/, // an aborted fetch
    /^(AbortError: )?The operation was aborted\.?\s*$/
];

// The OpenAPI client words an API error as `<error> (<status>)`, e.g. "Access Denied (403)".
const API_STATUS_PATTERN = /\(\d{3}\)$/;

function isNoResponseFetchMessage(message: unknown) {
    return typeof message === 'string' && NO_RESPONSE_FETCH_PATTERNS.some(re => re.test(message.trim()));
}

// Whether the original error, or anything in its `cause` chain, carries an HTTP response.
function hasResponseStatus(err: unknown): boolean {
    for (let e = err, depth = 0; e && typeof e === 'object' && depth < 5; e = (e as { cause?: unknown }).cause, depth++) {
        if (typeof (e as { response?: { status?: unknown } }).response?.status === 'number') return true;
    }
    return false;
}

/**
 * Sentry `beforeSend` filter for a fetch that failed before any HTTP response. Matches any linked
 * exception, so a wrapper such as "Failed to load endpoints" whose `cause` is that fetch is dropped
 * too — unless any linked exception shows the API answered with a status, which is always reported.
 */
export function excludeNoResponseFetchErrors(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
    const values = event.exception?.values;
    if (!values?.some(exception => isNoResponseFetchMessage(exception.value))) return event;
    if (values.some(exception => exception.value && API_STATUS_PATTERN.test(exception.value.trim()))) return event;
    if (hasResponseStatus(hint?.originalException)) return event;
    return null;
}
